import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const kvCacheComponent: ComponentDef = {
  id: 'kv-cache',
  name: 'KV Cache',
  description: 'Store and reuse previous key/value tensors during autoregressive inference.',
  category: 'optimization',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'n_heads', label: 'Query heads', type: 'number', default: 8, min: 1, max: 128, step: 1 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    {
      name: 'kv_variant',
      label: 'KV heads variant',
      type: 'select',
      default: 'mha',
      options: [
        { label: 'MHA (n_kv = n_heads)', value: 'mha' },
        { label: 'GQA (n_kv = n_heads/4)', value: 'gqa' },
        { label: 'MQA (n_kv = 1)', value: 'mqa' },
        { label: 'Standard KV Cache', value: 'standard' },
        { label: 'Prefix Caching (RadixAttention)', value: 'prefix_caching' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const n_heads = Number(params.n_heads);
    const seq_len = Number(params.seq_len);
    const d_head = d_model / n_heads;
    const kv_variant = String(params.kv_variant || 'mha');

    const n_kv_heads = kv_variant === 'mqa' ? 1
      : kv_variant === 'gqa' ? Math.max(1, Math.floor(n_heads / 4))
      : n_heads;

    const g = new GraphBuilder();

    if (kv_variant === 'standard') {
      // Standard KV Cache: cache K and V per layer, append new token's K/V each step, attend to full cached K/V
      g.input('x', [seq_len, d_model]);

      g.linear('wq', 'x', [d_model, n_heads * d_head]);
      g.linear('wk', 'x', [d_model, n_heads * d_head]);
      g.linear('wv', 'x', [d_model, n_heads * d_head]);

      g.reshape('q', 'wq', [n_heads, seq_len, d_head]);
      g.reshape('new_k', 'wk', [n_heads, seq_len, d_head]);
      g.reshape('new_v', 'wv', [n_heads, seq_len, d_head]);

      // Cached K/V from previous tokens
      g.input('cached_k', [n_heads, seq_len, d_head]);
      g.input('cached_v', [n_heads, seq_len, d_head]);

      // Store new K/V into cache
      g.cache('k_store', 'new_k', [n_heads, seq_len, d_head]);
      g.cache('v_store', 'new_v', [n_heads, seq_len, d_head]);

      // Append new K/V to full cache
      g.concat('all_k', ['cached_k', 'k_store'], [n_heads, seq_len * 2, d_head]);
      g.concat('all_v', ['cached_v', 'v_store'], [n_heads, seq_len * 2, d_head]);

      // Attention over full cached K/V
      g.transpose('kt', 'all_k', [n_heads, d_head, seq_len * 2], 'K^T');
      g.matmul('qk', 'q', 'kt', [n_heads, seq_len, seq_len * 2]);
      g.softmax('softmax', 'qk', -1);
      g.matmul('attn_v', 'softmax', 'all_v', [n_heads, seq_len, d_head]);

      g.reshape('out', 'attn_v', [seq_len, d_model]);
      g.output('out', 'out');

    } else if (kv_variant === 'prefix_caching') {
      // Prefix Caching (RadixAttention): build radix tree of shared KV prefixes across requests
      g.input('x', [seq_len, d_model]);

      g.linear('wq', 'x', [d_model, n_heads * d_head]);
      g.linear('wk', 'x', [d_model, n_heads * d_head]);
      g.linear('wv', 'x', [d_model, n_heads * d_head]);

      g.reshape('q', 'wq', [n_heads, seq_len, d_head]);
      g.reshape('new_k', 'wk', [n_heads, seq_len, d_head]);
      g.reshape('new_v', 'wv', [n_heads, seq_len, d_head]);

      // Shared prefix cache (radix tree node): K/V for shared prefix tokens
      g.input('shared_prefix_k', [n_heads, seq_len, d_head]);
      g.input('shared_prefix_v', [n_heads, seq_len, d_head]);

      // Fork at divergence point: per-request private K/V from previous steps
      g.input('private_k', [n_heads, seq_len, d_head]);
      g.input('private_v', [n_heads, seq_len, d_head]);

      // Append new token's K/V to per-request private segment
      g.cache('k_append', 'new_k', [n_heads, seq_len, d_head]);
      g.cache('v_append', 'new_v', [n_heads, seq_len, d_head]);

      // Concatenate private segment: old private + new append
      g.concat('private_full_k', ['private_k', 'k_append'], [n_heads, seq_len * 2, d_head]);
      g.concat('private_full_v', ['private_v', 'v_append'], [n_heads, seq_len * 2, d_head]);

      // Attention over shared prefix + private segments
      g.concat('all_k', ['shared_prefix_k', 'private_full_k'], [n_heads, seq_len * 3, d_head]);
      g.concat('all_v', ['shared_prefix_v', 'private_full_v'], [n_heads, seq_len * 3, d_head]);

      g.transpose('kt', 'all_k', [n_heads, d_head, seq_len * 3], 'K^T');
      g.matmul('qk', 'q', 'kt', [n_heads, seq_len, seq_len * 3]);
      g.softmax('softmax', 'qk', -1);
      g.matmul('attn_v', 'softmax', 'all_v', [n_heads, seq_len, d_head]);

      g.reshape('out', 'attn_v', [seq_len, d_model]);
      g.output('out', 'out');

    } else {
      // MHA / GQA / MQA variants
      g.input('x', [seq_len, d_model]);

      g.linear('wq', 'x', [d_model, n_heads * d_head]);
      g.linear('wk', 'x', [d_model, n_kv_heads * d_head]);
      g.linear('wv', 'x', [d_model, n_kv_heads * d_head]);

      g.reshape('q', 'wq', [n_heads, seq_len, d_head]);
      g.reshape('new_k', 'wk', [n_kv_heads, seq_len, d_head]);
      g.reshape('new_v', 'wv', [n_kv_heads, seq_len, d_head]);

      g.input('cached_k', [n_kv_heads, seq_len, d_head]);
      g.input('cached_v', [n_kv_heads, seq_len, d_head]);

      g.cache('k_store', 'new_k', [n_kv_heads, seq_len, d_head]);
      g.cache('v_store', 'new_v', [n_kv_heads, seq_len, d_head]);

      g.concat('all_k', ['cached_k', 'k_store'], [n_kv_heads, seq_len * 2, d_head]);
      g.concat('all_v', ['cached_v', 'v_store'], [n_kv_heads, seq_len * 2, d_head]);

      // For GQA/MQA: expand KV heads to match query heads before attention
      if (n_kv_heads < n_heads) {
        g.reshape('expand_k', 'all_k', [n_heads, seq_len * 2, d_head]);
        g.reshape('expand_v', 'all_v', [n_heads, seq_len * 2, d_head]);
        g.transpose('kt', 'expand_k', [n_heads, d_head, seq_len * 2], 'K^T');
        g.matmul('qk', 'q', 'kt', [n_heads, seq_len, seq_len * 2]);
        g.softmax('softmax', 'qk', -1);
        g.matmul('attn_v', 'softmax', 'expand_v', [n_heads, seq_len, d_head]);
      } else {
        g.transpose('kt', 'all_k', [n_heads, d_head, seq_len * 2], 'K^T');
        g.matmul('qk', 'q', 'kt', [n_heads, seq_len, seq_len * 2]);
        g.softmax('softmax', 'qk', -1);
        g.matmul('attn_v', 'softmax', 'all_v', [n_heads, seq_len, d_head]);
      }

      g.reshape('out', 'attn_v', [seq_len, d_model]);
      g.output('out', 'out');
    }

    return g.build();
  }
};
